import React, { useEffect, useState } from "react";
import { PatternFormat } from "react-number-format";
import { Box, TextField } from "@mui/material";
import { StyleSettings } from "../../styles/StyleSettings";
import { showDialog } from "@widgets/CommonDialog";

/**
 * 시분초 기간 입력 (from ~ to)
 * - SnPeriodBox와 같은 방식으로 cd_param 은 "tm_start,tm_end" 처럼 두 키를 콤마로 지정한다.
 * - 값은 "HH:mm:ss" 문자열(8자리)로 올라간다. defaultValue 는 "HH:mm:ss" / "HHmmss" 모두 허용한다.
 * - 포커스가 빠질 때 시(00~23)/분/초(00~59) 범위를 보정한 뒤 onValueChanged 를 호출한다.
 */
interface SnPeriodTimeBoxProps {
  defaultValue?: string; // "HH:mm:ss,HH:mm:ss"
  onValueChanged?: (start: string, end: string) => void;
  format?: string; // 기본 "##:##:##"
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  // true 면 종료 < 시작(자정 넘김)을 허용한다. 기본은 허용하지 않고 안내창을 띄운 뒤 종료값을 비운다.
  allowOvernight?: boolean;
}

const DEFAULT_FORMAT = "##:##:##";

// 숫자만 남긴다. "08:30:00" -> "083000"
const toDigits = (raw: any): string =>
  (raw ?? "").toString().replace(/[^0-9]/g, "");

// 숫자 6자리를 "HH:mm:ss" 로 만들고 범위를 보정한다. 빈 값은 "" 유지.
const normalizeTime = (raw: any): string => {
  const digits = toDigits(raw);
  if (!digits) return "";
  const padded = digits.padEnd(6, "0").slice(0, 6);
  const clamp = (v: string, max: number) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "00";
    return String(Math.min(n, max)).padStart(2, "0");
  };
  const hh = clamp(padded.slice(0, 2), 23);
  const mm = clamp(padded.slice(2, 4), 59);
  const ss = clamp(padded.slice(4, 6), 59);
  return `${hh}:${mm}:${ss}`;
};

const splitDefault = (defaultValue?: string): [string, string] => {
  const [s = "", e = ""] = (defaultValue ?? "").split(",");
  return [toDigits(s), toDigits(e)];
};

const SnPeriodTimeBox = React.memo(
  ({
    defaultValue,
    onValueChanged,
    format,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
    allowOvernight = false,
  }: SnPeriodTimeBoxProps) => {
    const pattern = format && format.includes("#") ? format : DEFAULT_FORMAT;
    const placeholder = pattern.replace(/#/g, "0");

    const [initStart, initEnd] = splitDefault(defaultValue);
    // 내부 값은 숫자만(HHmmss) 보관하고, 외부로는 "HH:mm:ss" 로 내보낸다.
    const [startValue, setStartValue] = useState<string>(initStart);
    const [endValue, setEndValue] = useState<string>(initEnd);
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    useEffect(() => {
      const [s, e] = splitDefault(defaultValue);
      setStartValue(s);
      setEndValue(e);
    }, [defaultValue]);

    useEffect(() => {
      setBorderWidth(isRequired ? "2px" : "1px");
    }, [isRequired]);

    useEffect(() => {
      setBackgroundColor(isEditable ? "white" : "#F1F1F1");
    }, [isEditable]);

    const emit = (nextStart: string, nextEnd: string) => {
      const s = normalizeTime(nextStart);
      let e = normalizeTime(nextEnd);

      // 종료시간이 시작시간보다 빠르면 안내 후 종료값을 비운다. (allowOvernight 가 true 면 검사하지 않는다)
      if (
        !allowOvernight &&
        s &&
        e &&
        Number(toDigits(e)) < Number(toDigits(s))
      ) {
        showDialog(
          "alter",
          `종료시간(${e})이 시작시간(${s})보다 빠릅니다.\n종료시간을 다시 입력해 주세요.`
        );
        e = "";
      }

      setStartValue(toDigits(s));
      setEndValue(toDigits(e));
      if (onValueChanged) onValueChanged(s, e);
    };

    const handleKeyDown = async (
      e: React.KeyboardEvent,
      target: "start" | "end"
    ) => {
      if (e.key === "Enter" || e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        if (target === "start") {
          // 시작 -> 종료 칸으로 포커스 이동
          const root = (e.target as HTMLElement).closest(
            "[data-sn-period-time]"
          );
          const inputs = root?.querySelectorAll("input");
          (inputs?.[1] as HTMLInputElement | undefined)?.focus();
          return;
        }
        if (isSearchPanel && e.key === "Enter") {
          onFocus && onFocus("current");
        } else {
          onFocus && onFocus("next");
        }
      }
    };

    const sx = {
      backgroundColor: backgroundColor,
      fontSize: 12,
      "& input": { textAlign: "center" },
      "& .MuiOutlinedInput-root": {
        "& fieldset": { borderWidth: borderWidth },
        "&:hover fieldset": { borderWidth: borderWidth },
        "&.Mui-focused fieldset": { borderWidth: borderWidth },
      },
    };

    const inputStyle = {
      height: StyleSettings.control_height,
      fontSize: StyleSettings.font,
    };

    return (
      <div
        data-sn-period-time
        style={{ display: "flex", alignItems: "center", gap: "15px" }}
      >
        <div style={{ width: "100%" }}>
          <PatternFormat
            value={startValue}
            customInput={TextField}
            format={pattern}
            mask="_"
            placeholder={placeholder}
            onValueChange={(values: any) => setStartValue(values.value || "")}
            onBlur={() => emit(startValue, endValue)}
            onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, "start")}
            fullWidth
            sx={sx}
            slotProps={{
              input: { inputRef: inputRef, style: inputStyle },
            }}
            disabled={isReadOnly}
          />
        </div>
        <Box> – </Box>
        <div style={{ width: "100%" }}>
          <PatternFormat
            value={endValue}
            customInput={TextField}
            format={pattern}
            mask="_"
            placeholder={placeholder}
            onValueChange={(values: any) => setEndValue(values.value || "")}
            onBlur={() => emit(startValue, endValue)}
            onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, "end")}
            fullWidth
            sx={sx}
            slotProps={{
              input: { style: inputStyle },
            }}
            disabled={isReadOnly}
          />
        </div>
      </div>
    );
  }
);

export default SnPeriodTimeBox;
